/**
 * O cliente único de dados: o mesmo Supabase do portal atual, sem uma segunda sessão na página.
 *
 * O PROBLEMA QUE ELE EVITA: o provedor de identidade (`packages/auth/src/providers/supabase.ts`)
 * já tem um cliente supabase-js com o GoTrueClient dele, guardando a sessão na chave padrão do
 * localStorage. Um segundo cliente comum na mesma página criaria um segundo GoTrueClient, lendo e
 * renovando a MESMA chave, e os dois disputariam a renovação do token.
 *
 * A SOLUÇÃO, medida na versão instalada (supabase-js 2.47.10, a do catálogo): a opção `accessToken`
 * (`dist/module/lib/types.d.ts:76`). Com ela o cliente NÃO cria GoTrueClient: o `auth` vira um
 * Proxy que recusa qualquer uso (`dist/module/SupabaseClient.js:60` a `69`), e cada requisição
 * pede o token a esta função (`_getAccessToken`, linha 174). Banco, Storage e Edge Functions
 * passam todos pelo mesmo `fetchWithAuth` (linhas 71, 88 e 95), então os três levam o token.
 *
 * A TELA NÃO ALCANÇA O CLIENTE: `FonteDeDados` é um objeto opaco, e o cliente mora num WeakMap
 * deste arquivo. Assim a tela só consegue o que os módulos oferecem, que é o que o ADR 0002 decide.
 */

// O SDK oficial: o único lugar do monorepo, fora do provedor de identidade, que abre o cliente.
import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/** O que o app entrega para criar a fonte. */
export interface OpcoesFonteDeDados {
  // A URL do projeto Supabase (produção ou homologação, conforme o ambiente do app).
  readonly url: string;
  // A chave publicável do projeto: identifica o projeto, e quem protege o dado é o RLS.
  readonly chave: string;
  // O token da sessão atual, lido do provedor de identidade a cada requisição; `null` sem sessão.
  readonly obterToken: () => Promise<string | null>;
  // O `fetch` que o cliente usa. O app não passa (vale o do navegador); a suíte passa um que anota
  // a requisição e responde sem rede, para provar a consulta exata de cada função contra o legado.
  readonly fetch?: typeof fetch;
}

/** A fonte de dados, opaca: a tela a guarda e a entrega aos módulos, e nada mais. */
export interface FonteDeDados {
  // Só um rótulo, para a fonte ser reconhecível em depuração; não dá acesso a nada.
  readonly tipo: 'fonte-de-dados-studiolu';
}

// O que cada fonte guarda por dentro, fora do alcance da tela.
interface Interno {
  readonly cliente: SupabaseClient;
  readonly obterToken: () => Promise<string | null>;
}

// WeakMap: a fonte descartada pelo app leva o cliente junto para a coleta de lixo.
const internos = new WeakMap<FonteDeDados, Interno>();

/**
 * Cria a fonte. Chamar UMA vez por app, na carga, como o provedor de identidade.
 *
 * Uso no app: `criarFonteDeDados({ url, chave, obterToken: async () =>
 * (await provedor.getSession())?.token ?? null })`.
 */
export function criarFonteDeDados(opcoes: OpcoesFonteDeDados): FonteDeDados {
  const cliente = createClient(opcoes.url, opcoes.chave, {
    // O token vem do provedor de identidade, e não de um GoTrueClient deste cliente.
    // Sem sessão, a chave publicável vai no lugar do token: é o que o próprio supabase-js faz
    // quando o token vem nulo (`dist/module/lib/fetch.js:35`), escrito aqui porque o tipo da
    // opção exige texto, e não `null`.
    accessToken: async () => (await opcoes.obterToken()) ?? opcoes.chave,
    auth: {
      // Redundantes com `accessToken` na 2.47.10, que nem cria o cliente de autenticação. Ficam
      // como segunda trava: se um dia alguém tirar o `accessToken`, este cliente continua sem
      // gravar sessão no localStorage e sem renovar token por conta própria.
      persistSession: false,
      autoRefreshToken: false,
    },
    // Só com `fetch` próprio (a suíte): sem ele, o supabase-js usa o `fetch` global.
    ...(opcoes.fetch ? { global: { fetch: opcoes.fetch } } : {}),
  });
  // O objeto que a tela recebe, congelado para ninguém pendurar nada nele.
  const fonte: FonteDeDados = Object.freeze({ tipo: 'fonte-de-dados-studiolu' as const });
  internos.set(fonte, { cliente, obterToken: opcoes.obterToken });
  return fonte;
}

/** O cliente de uma fonte. Uso interno dos módulos; não sai pelo `index.ts`. */
export function clienteDe(fonte: FonteDeDados): SupabaseClient {
  const interno = internos.get(fonte);
  // Fonte que não nasceu de `criarFonteDeDados` é erro de programação, e ele aparece na hora.
  if (!interno) throw new Error('Fonte de dados desconhecida: crie-a com criarFonteDeDados.');
  return interno.cliente;
}

/** O token atual de uma fonte, para a única chamada do legado que o envia explícito. */
export async function tokenDe(fonte: FonteDeDados): Promise<string | null> {
  const interno = internos.get(fonte);
  // Mesmo erro de programação de `clienteDe`.
  if (!interno) throw new Error('Fonte de dados desconhecida: crie-a com criarFonteDeDados.');
  return interno.obterToken();
}
